import React from 'react';
import { Nullish } from 'shared/utils/type-utils';
import styled from 'styled-components';
import TinyColor from 'tinycolor2';
import Link from 'next/link';

import { EmbeddedExternalLink } from 'src/components/core';

type HighlightCardProps = {
  title: string;
  icon: JSX.Element;
  message: React.ReactNode;
  customColor: Nullish | string;
  href: string;
  isEmbeddedCarousel: boolean;
  testId?: Nullish | string;
};

export const HighlightCard = ({
  title,
  icon,
  message,
  customColor,
  href,
  isEmbeddedCarousel,
  testId,
}: HighlightCardProps): JSX.Element => {
  const LinkWrapper = isEmbeddedCarousel ? EmbeddedExternalLink : Link;

  return (
    <LinkWrapper href={href}>
      <Wrapper data-testid={testId} $customColor={customColor}>
        <IconWrapper>{icon}</IconWrapper>
        <Title>{title}</Title>
        <Message>{message}</Message>
      </Wrapper>
    </LinkWrapper>
  );
};

const Wrapper = styled.div<{ $customColor: Nullish | string }>`
  --color-dark: ${({ $customColor, theme }) => {
    const color: string = $customColor || theme.colors.primaryBlue;
    return TinyColor.mix(color, '#000', 20).toString();
  }};

  --color-light: ${({ $customColor, theme }) => {
    const color: string = $customColor || theme.colors.primaryBlue;
    return `${color}1a`;
  }};

  background: var(--color-light);
  color: var(--color-dark);
  border: 1px solid #e3e7e9;
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  padding-top: 72px;
  text-align: left;
  min-height: 100%;
  width: 100%;
  min-width: 188px;

  &:hover {
    cursor: pointer;
  }

  svg {
    color: var(--color-dark);
  }
`;

const IconWrapper = styled.div`
  display: flex;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h3`
  font-size: 26px;
  font-weight: 600;
  line-height: 30px;
`;

const Message = styled.span`
  font-size: 14px;
  line-height: 17px;
`;
